/**
 * Authenticated app shell: a slim brand header above a vertical area sidebar
 * and the active page.
 *
 * Pages rendered inside this shell must not draw their own app header or their
 * own nav — the shell owns both. The header is sticky and a fixed `h-14`; the
 * sidebar is sticky directly beneath it. Those two facts are coupled
 * (`app-sidebar.tsx` offsets by `top-14`), so the header's height is not a
 * free-floating number.
 *
 * **Why the window still scrolls.** The obvious way to build a rail-plus-content
 * shell is `h-screen overflow-hidden` with an inner scroll container. That is
 * not what happens here: several boards pin their own headers and DataGrid
 * columns to `top-0`, which resolves against the nearest scroll container, so
 * moving the scroll inside would silently re-anchor them. The sidebar is
 * `sticky` inside a normally-scrolling document instead, and it scrolls
 * internally when the ten areas outgrow the viewport.
 *
 * Below `lg` the rail is replaced by a left-edge drawer opened from the header,
 * so a 16rem column never eats the content it exists to navigate.
 */

import { Button } from "@norican/react";
import { Link, Outlet, useNavigate } from "react-router";

import { AppSidebar, AppSidebarDrawer } from "@/components/app-sidebar";
import { NoricanLogo } from "@/components/norican-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useLogout, usePrefetchColdBoards } from "@/lib/api";
import { PRODUCT_EXPANSION, PRODUCT_NAME } from "@/lib/brand";

export function AppShell() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  // Warm the two boards the landing page doesn't already read, so no board
  // opens on a skeleton after the first few seconds of a session.
  usePrefetchColdBoards();

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <div className="bg-bg min-h-screen">
      {/* The bar must sit above DataGrid's sticky header and pinned columns,
          which the design system puts at `--norican-z-sticky` (1100). Tailwind's
          `z-10` lost to them, so status dots and pinned cells scrolled over the
          navbar. Offset from the token rather than hard-coding a number, and
          stay below `--norican-z-dropdown` (1200) so menus still overlay us. */}
      <header className="bg-bg border-border sticky top-0 z-[calc(var(--norican-z-sticky)+10)] flex h-14 items-center gap-x-4 border-b px-4 sm:px-6">
        {/* Drawer trigger first, so the nav is the first thing in the tab order
            on the viewports where it is not permanently on screen. */}
        <AppSidebarDrawer />
        {/* Corporate mark, then the product. The hairline divider keeps the
            two identities distinct — Norican is the company, NOVA is the
            product — instead of reading as one long wordmark. */}
        <Link
          to="/overview"
          className="flex items-center gap-3 no-underline"
          aria-label={`${PRODUCT_NAME} — ${PRODUCT_EXPANSION}`}
        >
          <NoricanLogo height={26} />
          <span className="bg-border hidden h-6 w-px sm:block" aria-hidden="true" />
          <span className="flex items-baseline gap-3">
            <span className="font-display text-fg text-xl font-bold">{PRODUCT_NAME}</span>
            <span className="text-fg-muted hidden text-sm lg:inline">{PRODUCT_EXPANSION}</span>
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <Button
            variant="outline"
            tone="neutral"
            loading={logoutMutation.isPending}
            onClick={handleLogout}
          >
            Sign out
          </Button>
        </div>
      </header>
      <div className="flex items-start">
        <AppSidebar />
        {/* A plain `div`, not a `main`: every board page renders its own
            `<main className="mx-auto max-w-7xl …">`, and `main` may not nest.
            `min-w-0` so a wide board (DataGrid, long chart legend) scrolls
            inside the content column instead of stretching the flex row and
            pushing the rail off screen. */}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
